import { useQuery } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints } from '@/lib/api/endpoints';
import { serviceAreaKeys } from '@/lib/queryKeys';
import type { ServiceArea } from '@/types/service-area';

interface RawServiceArea {
  id?: string;
  zipCode: string;
  city?: string;
  stateCode?: string;
  state?: string;
  areaType?: string;
  active?: boolean;
  isActive?: boolean;
}

/**
 * Shared fetcher — exported so callers like useDeleteTaxZoneWithArea can pass
 * it directly to queryClient.fetchQuery(), guaranteeing a queryFn is always
 * present even on a cold cache.
 *
 * ONE source: coverage lives in a single table now, so this is a plain fetch.
 * The old ops+scheduler merge (with its `source: 'operations' | 'scheduler' |
 * 'both'` tags and per-source ids) existed only to reconcile two stores that
 * could disagree; there is nothing left to reconcile.
 */
export async function fetchServiceAreas(): Promise<ServiceArea[]> {
  const areas = await apiGet<RawServiceArea[]>(operationsClient, serviceAreaEndpoints.list());

  return areas
    .filter((a) => a.zipCode)
    .map((a) => ({
      id: a.id ?? a.zipCode,
      zipCode: a.zipCode,
      city: a.city ?? '',
      state: a.stateCode ?? a.state ?? '',
      areaType: a.areaType,
      active: a.active ?? a.isActive ?? true,
    }))
    .sort((a, b) => a.zipCode.localeCompare(b.zipCode));
}

/** The tenant's configured coverage areas. */
export function useServiceAreas() {
  return useQuery<ServiceArea[]>({
    queryKey: serviceAreaKeys.list(),
    queryFn: fetchServiceAreas,
  });
}
